import { Vector as VectorLayer } from 'ol/layer';
import { Style, Fill, Stroke } from 'ol/style';
import CircleStyle from 'ol/style/Circle';

export function createHotspotLayer(options) {
    let source = options.source;
    let bandwidth = options.bandwidth > 0 ? options.bandwidth : 1000;
    let threshold = options.threshold != null ? options.threshold : 2.0;
    let symbolSize = options.symbolSize > 0 ? options.symbolSize : 6;
    let hotColor = options.hotColor || '#d7191c';
    let coldColor = options.coldColor || '#2c7bb6';
    let neutralColor = options.neutralColor;
    let spatialWeight = options.spatialWeight === 'INVERSE_DISTANCE' ? 'INVERSE_DISTANCE' : 'BINARY';
    let weightFn = typeof options.weight === 'function' ? options.weight : function () { return 1; };

    let strokeWhite = new Stroke({ color: '#ffffff', width: 1 });
    let hotStyle = new Style({ image: new CircleStyle({ radius: symbolSize, fill: new Fill({ color: hotColor }), stroke: strokeWhite }) });
    let coldStyle = new Style({ image: new CircleStyle({ radius: symbolSize, fill: new Fill({ color: coldColor }), stroke: strokeWhite }) });
    let neutralStyle = neutralColor != null
        ? new Style({ image: new CircleStyle({ radius: symbolSize, fill: new Fill({ color: neutralColor }), stroke: strokeWhite }) })
        : null;

    let layer = new VectorLayer({
        source: source,
        zIndex: options.zIndex,
        visible: options.visible,
        style: function (feature) {
            let z = feature.get('_hs_z');
            if (z == null) return null;
            if (z >= threshold) return hotStyle;
            if (z <= -threshold) return coldStyle;
            return neutralStyle;
        }
    });

    let classifyScheduled = false;
    function scheduleClassify() {
        if (classifyScheduled) return;
        classifyScheduled = true;
        setTimeout(function () {
            classifyScheduled = false;
            _classify(source, bandwidth, weightFn, spatialWeight);
            layer.changed();
        }, 50);
    }

    source.on('change', function () {
        if (source.getState() === 'ready') scheduleClassify();
    });
    source.on('addfeature', scheduleClassify);
    source.on('removefeature', scheduleClassify);
    source.on('changefeature', scheduleClassify);

    if (source.getFeatures().length > 0) {
        _classify(source, bandwidth, weightFn, spatialWeight);
    }

    return layer;
}

function _classify(source, bandwidth, weightFn, spatialWeight) {
    let features = source.getFeatures();
    let n = features.length;
    if (n === 0) return;

    let coords = new Array(n);
    let weights = new Array(n);
    let indexByFeature = new Map();

    for (let i = 0; i < n; i++) {
        let f = features[i];
        indexByFeature.set(f, i);
        coords[i] = _featureCoord(f.getGeometry());
        let w = weightFn(f);
        if (w == null || isNaN(w)) w = 0;
        weights[i] = w;
    }

    let bwSq = bandwidth * bandwidth;
    let useInverse = spatialWeight === 'INVERSE_DISTANCE';
    let localSums = new Array(n);

    for (let i = 0; i < n; i++) {
        let c = coords[i];
        if (c == null) { localSums[i] = 0; continue; }
        let bbox = [c[0] - bandwidth, c[1] - bandwidth, c[0] + bandwidth, c[1] + bandwidth];
        let candidates = source.getFeaturesInExtent(bbox);
        let sum = 0;
        for (let k = 0; k < candidates.length; k++) {
            let jIdx = indexByFeature.get(candidates[k]);
            if (jIdx == null) continue;
            let cj = coords[jIdx];
            if (cj == null) continue;
            let dx = c[0] - cj[0];
            let dy = c[1] - cj[1];
            let distSq = dx * dx + dy * dy;
            if (distSq > bwSq) continue;
            if (useInverse) {
                let dist = Math.sqrt(distSq);
                sum += (1 - dist / bandwidth) * weights[jIdx];
            } else {
                sum += weights[jIdx];
            }
        }
        localSums[i] = sum;
    }

    let mean = 0;
    for (let i = 0; i < n; i++) mean += localSums[i];
    mean /= n;

    let variance = 0;
    for (let i = 0; i < n; i++) {
        let d = localSums[i] - mean;
        variance += d * d;
    }
    variance /= n;
    let stddev = Math.sqrt(variance);

    for (let i = 0; i < n; i++) {
        let z = stddev === 0 ? 0 : (localSums[i] - mean) / stddev;
        features[i].set('_hs_z', z, true);
    }
}

function _featureCoord(geom) {
    if (geom == null) return null;
    let type = geom.getType();
    if (type === 'Point') return geom.getCoordinates();
    if (type === 'MultiPoint') {
        let pts = geom.getCoordinates();
        return pts.length > 0 ? pts[0] : null;
    }
    if (typeof geom.getInteriorPoint === 'function') {
        return geom.getInteriorPoint().getCoordinates();
    }
    let ext = geom.getExtent();
    if (ext == null) return null;
    return [(ext[0] + ext[2]) / 2, (ext[1] + ext[3]) / 2];
}
